import React from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Phone } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DealStageChip from "@/components/deals/DealStageChip";
import ThreadOptionsMenu from "@/components/messages/ThreadOptionsMenu";
import { useCallDialog } from "@/context/CallDialogContext";
import { ContactType } from "@/enum/ContactType";
import type Contact from "@/model/interface/Contact";
import type ContactPhone from "@/model/interface/ContactPhone";
import { phoneSkipLabel } from "@/util/dialer";
import { getContactDisplayName, getInitials } from "@/util/string";
import { toUsE164 } from "@/util/validation";

interface InboxThreadHeaderProps {
  contact?: Contact | null;
  conversationPhoneNumber?: string;
  conversationId?: string;
  flagged?: boolean;
  isGroup: boolean;
  showsOptions: boolean;
  onText: (phoneNumber: string) => void;
  onArchived: () => void;
}

function dialNumberOf(phone: ContactPhone): string | undefined {
  return phone.phoneNumberE164 || phone.phoneNumber;
}

function isSameNumber(phone: ContactPhone, phoneNumber?: string): boolean {
  const dialNumber = dialNumberOf(phone);
  if (!dialNumber || !phoneNumber) {
    return false;
  }
  return toUsE164(dialNumber) === toUsE164(phoneNumber);
}

function phoneTypeLabel(phone: ContactPhone): string {
  if (!phone.phoneType) {
    return "Phone";
  }
  return phone.phoneType.charAt(0) + phone.phoneType.slice(1).toLowerCase();
}

const InboxThreadHeader: React.FC<InboxThreadHeaderProps> = ({
  contact,
  conversationPhoneNumber,
  conversationId,
  flagged,
  isGroup,
  showsOptions,
  onText,
  onArchived,
}) => {
  const { setPendingDialNumber, openCallDialog } = useCallDialog();

  const phones = (contact?.phones ?? []).filter((phone) => !!dialNumberOf(phone));
  const conversationPhone = phones.find((phone) => isSameNumber(phone, conversationPhoneNumber));
  const orderedPhones = conversationPhone
    ? [conversationPhone, ...phones.filter((phone) => phone !== conversationPhone)]
    : phones;
  const primaryNumber = conversationPhoneNumber ?? (orderedPhones[0] ? dialNumberOf(orderedPhones[0]) : undefined);
  const displayNumber = conversationPhone?.phoneNumber || primaryNumber;
  const name = getContactDisplayName(contact) ?? displayNumber ?? "Conversation";
  const initials = contact ? getInitials(contact.firstName, contact.lastName) : "?";
  const stage = contact?.activeDeal?.stage;
  const showsCall = !isGroup && !!primaryNumber;

  const call = (phoneNumber?: string) => {
    if (!phoneNumber) {
      return;
    }
    setPendingDialNumber(phoneNumber);
    openCallDialog();
  };

  return (
    <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-2.5">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar className="h-9 w-9 shrink-0">
          {contact?.photo && <AvatarImage src={contact.photo} />}
          <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col gap-0.5">
          {contact?.id ? (
            <Link
              to={`/contacts/${contact.id}/detail`}
              className="truncate text-sm font-semibold hover:underline"
              title="Open contact"
            >
              {name}
            </Link>
          ) : (
            <span className="truncate text-sm font-semibold">{name}</span>
          )}
          <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
            {displayNumber && displayNumber !== name && <span className="truncate tabular-nums">{displayNumber}</span>}
            {contact?.type === ContactType.LEAD && <span className="status-pill-neutral shrink-0">Lead</span>}
            {stage && (
              <span className="shrink-0">
                <DealStageChip stage={stage} />
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {showsCall && orderedPhones.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-10 w-10 rounded-full" title="Call" aria-label={`Call ${name}`}>
                <Phone className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Call {name}
              </DropdownMenuLabel>
              {orderedPhones.map((phone) => {
                const skipLabel = phoneSkipLabel(phone);
                return (
                  <DropdownMenuItem
                    key={phone.id}
                    onClick={() => call(dialNumberOf(phone))}
                    className="flex items-center gap-2.5"
                  >
                    <Phone className="h-3.5 w-3.5 shrink-0" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="text-sm font-medium">{phoneTypeLabel(phone)}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {phone.phoneNumber || phone.phoneNumberE164}
                      </span>
                    </span>
                    {phone === conversationPhone && <span className="status-pill-info shrink-0">This conversation</span>}
                    {skipLabel && <span className="shrink-0 text-xs text-destructive">{skipLabel}</span>}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {showsCall && orderedPhones.length <= 1 && (
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 rounded-full"
            title={`Call ${displayNumber}`}
            aria-label={`Call ${displayNumber}`}
            onClick={() => call(primaryNumber)}
          >
            <Phone className="h-4 w-4" />
          </Button>
        )}
        {primaryNumber && (
          <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 rounded-full"
            title="Text"
            aria-label="Write a text"
            onClick={() => onText(primaryNumber)}
          >
            <MessageSquare className="h-4 w-4" />
          </Button>
        )}
        {showsOptions && (!!contact?.id || !!conversationId) && (
          <ThreadOptionsMenu
            contactId={contact?.id}
            conversationId={conversationId}
            flagged={flagged}
            onArchived={onArchived}
          />
        )}
      </div>
    </div>
  );
};

export default InboxThreadHeader;
